// useReportEditor.ts
import { useState, useEffect, useMemo } from "react";
import { MainService } from "@/shared/services/main.service";
import {
  replaceCodesWithValues,
  generateAndUploadReportPdf,
  previewReportPdf,
} from "../utils/pdfGenerator";
import type { Report, TemplateCodeBasic, Cover } from "@/shared/types";

export interface ReportFormData {
  nombre: string;
  activo: boolean;
  precio: number;
  moneda: string;
  sectorEmpresa: string;
  bonoAjustado: string;
  contenido: string;
  contentEditor: string;
  linkPago: string;
  escenario: string;
  portadaId: number | null;
  type: "valora" | "kapital";
}

const INITIAL_FORM: ReportFormData = {
  nombre: "",
  activo: true,
  precio: 0,
  moneda: "",
  sectorEmpresa: "",
  bonoAjustado: "",
  contenido: "",
  contentEditor: "",
  linkPago: "",
  escenario: "",
  portadaId: null,
  type: "kapital",
};

const DEFAULT_CONTENT = "";

function reportToForm(report: Report): ReportFormData {
  return {
    nombre: report.nombre,
    activo: report.activo,
    precio: report.precio ?? 0,
    moneda: report.moneda,
    sectorEmpresa: report.sector_empresa ?? "",
    bonoAjustado: report.bono_ajustado ?? "",
    contenido: report.contenido ?? "",
    contentEditor: (report as any).contentEditor ?? "",
    linkPago: report.link_pago ?? "",
    escenario: report.escenario ?? "",
    portadaId: report.portada?.id ?? null,
    type: report.type ?? "kapital",
  };
}

export function useReportEditor(id?: string) {
  const isEdit = Boolean(id);
  const [form, setForm] = useState<ReportFormData>(INITIAL_FORM);
  const [report, setReport] = useState<Report | null>(null);
  const [covers, setCovers] = useState<Cover[]>([]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [editorKey, setEditorKey] = useState(0);
  const [editorContent, setEditorContent] = useState<string>("");
  const [contentReady, setContentReady] = useState(false);
  const [sourceOriginalName, setSourceOriginalName] = useState<string | null>(null);

  const [currentTemplateCodes, setCurrentTemplateCodes] = useState<
    TemplateCodeBasic[] | null
  >(null);
  const [codesLoading, setCodesLoading] = useState(false);

  // Obtiene la lista de portadas
  useEffect(() => {
    MainService.getCovers().then(setCovers);
  }, []);

  // Obtiene los códigos de plantilla maestros
  useEffect(() => {
    setCodesLoading(true);
    MainService.getCurrentMasterTemplateCodes()
      .then((res) => {
        const codes: TemplateCodeBasic[] = [];
        const grouped = res?.extracted_codes || {};
        ["kapital", "valora"].forEach((t) => {
          const list = grouped[t] || [];
          for (const item of list) {
            if (!item) continue;
            if (typeof item === "string") {
              codes.push({
                id: -1,
                nombre: item,
                code: item,
                type: t as any,
                value: null,
                hoja: null,
              });
            } else if (item.code) {
              const nombre =
                item.nombre ?? item.original_name ?? item.filename ?? item.code;
              codes.push({
                id: item.id ?? -1,
                nombre,
                code: item.code,
                type: t as any,
                hoja: item.hoja ?? null,
                value: item.value ?? null,
                template_code_image_url: item.template_code_image_url,
                template_code_image_id: item.template_code_image_id ?? null,
              });
            }
          }
        });
        setCurrentTemplateCodes(codes);
      })
      .catch(() => setCurrentTemplateCodes([]))
      .finally(() => setCodesLoading(false));
  }, []);

  // Obtiene los datos del reporte seleccionado
  useEffect(() => {
    if (!id) {
      setContentReady(true);
      return;
    }
    setLoading(true);
    MainService.getReport(Number(id))
      .then(async (r) => {
        setReport(r);
        setSourceOriginalName((r as any).source_original_name ?? null);
        setForm(reportToForm(r));
        if (r.file) {
          try {
            const html = await MainService.getReportContent(Number(id));
            setEditorContent(html || DEFAULT_CONTENT);
          } catch {
            setEditorContent(DEFAULT_CONTENT);
          }
        } else {
          setEditorContent((r as any).contentEditor ?? DEFAULT_CONTENT);
        }
        setEditorKey((k) => k + 1);
        setContentReady(true);
      })
      .catch(() => setError("No se pudo cargar el reporte."))
      .finally(() => setLoading(false));
  }, [id]);

  const handleChange = <K extends keyof ReportFormData>(
    key: K,
    value: ReportFormData[K]
  ) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const templateCodes =
    report?.template?.template_codes ?? currentTemplateCodes ?? [];
  const portadaUrl = report?.portada?.portada?.url;
  const selectedCover = form.portadaId
    ? covers.find((c) => c.id === form.portadaId)
    : undefined;
  const selectedCoverUrl = selectedCover?.portada?.url ?? portadaUrl ?? null;

  const compatibleCovers = useMemo(
    () => covers.filter((cover) => cover.producto === form.type),
    [covers, form.type]
  );

  useEffect(() => {
    if (compatibleCovers.length === 0) return;
    const current = compatibleCovers.find(
      (cover) => cover.id === form.portadaId
    );
    if (!current) {
      setForm((prev) => ({ ...prev, portadaId: compatibleCovers[0].id }));
    }
  }, [form.type, form.portadaId, compatibleCovers]);

  const handleAddCode = (codeObj: TemplateCodeBasic) => {
    let displayValue = "N/D";
    if (codeObj.value !== undefined && codeObj.value !== null) {
      displayValue = !isNaN(Number(codeObj.value))
        ? Number(codeObj.value).toFixed(2)
        : String(codeObj.value);
    }
    const hoverText = `${codeObj.nombre}: ${displayValue}`;
    const codeHtml = `<span class="editor-code-tag" title="${hoverText}" style="background-color: #f1f5f9; color: #3b82f6; padding: 2px 6px; border-radius: 4px; border: 1px solid #bfdbfe; font-family: monospace; font-size: 11px; cursor: help;">${codeObj.code}</span>`;

    setEditorContent((prev) => {
      const trimmed = prev.trim();
      const lastTagMatch = trimmed.match(/(<\/[a-zA-Z0-9]+>)$/i);
      if (lastTagMatch && lastTagMatch.index !== undefined) {
        return (
          trimmed.substring(0, lastTagMatch.index) +
          codeHtml +
          trimmed.substring(lastTagMatch.index)
        );
      }
      return prev + codeHtml;
    });
    setEditorKey((k) => k + 1);
  };

  const handleSave = async () => {
    setSaving(true);
    let createdReportId: number | undefined;
    try {
      let reportId = id ? Number(id) : undefined;
      const reportPayload = {
        nombre: form.nombre,
        activo: form.activo,
        precio: form.precio,
        moneda: form.moneda,
        sector_empresa: form.sectorEmpresa || null,
        bono_ajustado: form.bonoAjustado || null,
        contenido: form.contenido,
        link_pago: form.linkPago || null,
        escenario: form.escenario || null,
        type: form.type,
        contentEditor: editorContent,
        portada_id: form.portadaId,
      };

      if (!reportId) {
        const created = await MainService.createReport(reportPayload);
        reportId = created.id;
        createdReportId = created.id;
      } else {
        await MainService.updateReport(Number(reportId), reportPayload);
      }

      // Las plantillas DOCX/PDF se previsualizan y se convierten en backend.
      // No subir un PDF generado desde HTML porque reemplazaría la referencia
      // de previsualización nativa y además exige contenido HTML válido.
      if (reportId && !sourceOriginalName) {
        const finalHtmlForPdf = replaceCodesWithValues(
          editorContent,
          templateCodes
        );
        await generateAndUploadReportPdf(
          reportId,
          finalHtmlForPdf,
          editorContent,
          selectedCoverUrl
        );
      }
      return true; // Retorna verdadero si tiene éxito
    } catch (err: any) {
      console.error(err);
      if (createdReportId) {
        try {
          await MainService.deleteReport(createdReportId);
        } catch (cleanupError) {
          console.error("No se pudo revertir el reporte creado", cleanupError);
        }
      }
      return false; // Retorna falso si falla
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async () => {
    setSaving(true);
    try {
      if (sourceOriginalName && id) {
        const blob = await MainService.previewReportSource(Number(id));
        const url = URL.createObjectURL(blob);
        // Navegar la pestaña actual evita popups about:blank bloqueados por
        // el navegador después de la conversión asíncrona.
        window.location.assign(url);
        window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
        return;
      }
      const finalHtmlForPdf = replaceCodesWithValues(
        editorContent,
        templateCodes
      );
      await previewReportPdf(finalHtmlForPdf, selectedCoverUrl, false);
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleUploadSource = async (file: File) => {
    if (!id) throw new Error("Guarda el reporte antes de subir una plantilla");
    setSaving(true);
    try {
      const result = await MainService.uploadReportSource(Number(id), file);
      // La plantilla DOCX/PDF se conserva como archivo nativo. No cargar su
      // extracción HTML en el editor porque altera el diseño original.
      setSourceOriginalName(result.source_original_name || file.name);
      setContentReady(true);
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveSource = async () => {
    if (!id) return;
    setSaving(true);
    try {
      await MainService.deleteReportSource(Number(id));
      setSourceOriginalName(null);
      setEditorContent("");
      setEditorKey((k) => k + 1);
    } finally {
      setSaving(false);
    }
  };

  return {
    isEdit,
    form,
    covers: compatibleCovers,
    loading,
    saving,
    error,
    editorKey,
    editorContent,
    contentReady,
    templateCodes,
    codesLoading,
    selectedCoverUrl,
    sourceOriginalName,
    handleChange,
    handleAddCode,
    handleSave,
    handlePreview,
    handleUploadSource,
    handleRemoveSource,
    setEditorContent,
  };
}
